import React from "react";
import { Box, CircularProgress, Paper, Tab, Tabs } from "@mui/material";
import { motion } from "framer-motion";
import {
  PieChart as PieChartIcon,
  TimelineOutlined as TimelineIcon,
} from "@mui/icons-material";

import ChartTypeSelector from "../../ui/ChartTypeSelector";
import CategoryChart from "./CategoryChart";
import WeeklyChart from "./WeeklyChart";
import TabPanel from "../../TabPanel/TabPanel";

/**
 * Component for displaying cost charts with tabs
 * @param {Object} props - Component props
 * @param {Array} props.categorySummary - Category summary data
 * @param {Array} props.weeklySummary - Weekly summary data
 * @param {string} props.chartType - Current chart type
 * @param {number} props.activeTab - Currently active tab index
 * @param {Array} props.chartColors - Array of colors for charts
 * @param {Function} props.renderCustomizedPieLabel - Custom label renderer for pie charts
 * @param {Function} props.handleTabChange - Tab change handler
 * @param {Function} props.handleChartTypeChange - Chart type change handler
 * @param {Function} props.getCategoryChartData - Function to process category chart data
 * @param {Function} props.getWeeklyChartData - Function to process weekly chart data
 * @param {boolean} props.loading - Loading state
 * @returns {JSX.Element} Charts component with tabs
 */
const CostsCharts = ({
  categorySummary,
  weeklySummary,
  chartType,
  activeTab,
  chartColors,
  renderCustomizedPieLabel,
  handleTabChange,
  handleChartTypeChange,
  getCategoryChartData,
  getWeeklyChartData,
  loading,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.5 }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 0,
          mb: 4,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              label="Custo por Categoria"
              icon={<PieChartIcon />}
              iconPosition="start"
            />
            <Tab
              label="Tendência Semanal"
              icon={<TimelineIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 3, height: 400, position: "relative" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Chart Type Selector */}
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  zIndex: 10,
                }}
              >
                <ChartTypeSelector
                  chartType={chartType}
                  onChartTypeChange={handleChartTypeChange}
                />
              </Box>

              {/* Category Tab */}
              <TabPanel value={activeTab} index={0}>
                <Box sx={{ width: "100%", height: "100%" }}>
                  <CategoryChart
                    data={getCategoryChartData(categorySummary)}
                    chartType={chartType}
                    chartColors={chartColors}
                    renderCustomizedPieLabel={renderCustomizedPieLabel}
                  />
                </Box>
              </TabPanel>

              {/* Weekly Tab */}
              <TabPanel value={activeTab} index={1}>
                <Box sx={{ width: "100%", height: "100%" }}>
                  <WeeklyChart
                    data={getWeeklyChartData(weeklySummary)}
                    chartType={chartType}
                    chartColors={chartColors}
                    renderCustomizedPieLabel={renderCustomizedPieLabel}
                  />
                </Box>
              </TabPanel>
            </>
          )}
        </Box>
      </Paper>
    </motion.div>
  );
};

export default CostsCharts;
